/**
 * localPApi — RTK Query slice cho /purchase/local-p/*.
 * Tag types: 'LocalP' (list) + 'LocalPDetail' (single).
 */
import { safePathEncode } from '@/utils/safePath'
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPCreateIn,
  LocalPDetail,
  LocalPListItem,
  LocalPListQuery,
  LocalPPrintChooseData,
  LocalPPrintChooseIn,
  LocalPPrintKind,
  LocalPPrintResult,
  LocalPUpdateIn,
  MessageResponse,
  PageResponse,
} from '@/types/api'

export const localPApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listLocalP: b.query<PageResponse<LocalPListItem>, LocalPListQuery | void>({
      query: (params) => ({ url: '/purchase/local-p/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'LocalP' as const, id: it.contract_no })),
              { type: 'LocalP' as const, id: 'LIST' },
            ]
          : [{ type: 'LocalP' as const, id: 'LIST' }],
    }),
    suggestLocalPContractNo: b.query<{ contractNo: string }, void>({
      query: () => '/purchase/local-p/suggest-contract-no',
      transformResponse: (r: { suggestedNo?: string; suggested_no?: string }) => ({
        contractNo: r.suggestedNo ?? r.suggested_no ?? '',
      }),
      providesTags: [{ type: 'LocalP' as const, id: 'SUGGEST' }],
    }),
    /** Pre-save check (skill access-suggest): contract_no đã có active row chưa? */
    checkLocalPContractNoExists: b.query<{ exists: boolean }, { contractNo: string }>({
      query: ({ contractNo }) =>
        `/purchase/local-p/check-contract-no?contractNo=${encodeURIComponent(contractNo)}`,
      keepUnusedDataFor: 0,
    }),
    getLocalP: b.query<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => `/purchase/local-p/${safePathEncode(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [
        { type: 'LocalPDetail', id: contractNo },
      ],
    }),
    createLocalP: b.mutation<DataResponse<LocalPDetail>, LocalPCreateIn>({
      query: (body) => ({
        url: '/purchase/local-p/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LocalP', id: 'LIST' }, { type: 'ContractAll', id: 'LIST' }],
    }),
    updateLocalP: b.mutation<
      DataResponse<LocalPDetail>,
      { contractNo: string; body: LocalPUpdateIn }
    >({
      query: ({ contractNo, body }) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalP', id: arg.contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: arg.contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    deleteLocalP: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'LocalP', id: contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    copyLocalP: b.mutation<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/copy`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'LocalP', id: 'LIST' }, { type: 'ContractAll', id: 'LIST' }],
    }),
    forceCloseLocalP: b.mutation<
      DataResponse<LocalPDetail>,
      { contractNo: string; note?: string }
    >({
      query: ({ contractNo, note }) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/force-close`,
        method: 'POST',
        body: { note: note ?? '' },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalP', id: arg.contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: arg.contractNo },
      ],
    }),
    /** Cancel contract — sets cancel_adjust = 0 (NOT a hard delete). */
    cancelLocalP: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${safePathEncode(contractNo)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'LocalP', id: contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    /** Access evidence: form_frmPurchaseQryLocalPPr_Choose.txt. */
    getLocalPPrintChoose: b.query<DataResponse<LocalPPrintChooseData>, string>({
      query: (contractNo) => `/purchase/local-p/${safePathEncode(contractNo)}/print/contract/choose`,
    }),
    /** Sprint 6 sẽ render thật; Sprint 1 placeholder. */
    printLocalP: b.query<
      DataResponse<LocalPPrintResult>,
      { contractNo: string; kind: LocalPPrintKind; choose?: LocalPPrintChooseIn }
    >({
      query: ({ contractNo, kind, choose }) =>
        choose && kind === 'contract'
          ? {
              url: `/purchase/local-p/${safePathEncode(contractNo)}/print/contract/preview`,
              method: 'POST',
              body: choose,
            }
          : `/purchase/local-p/${safePathEncode(contractNo)}/print/${kind}`,
    }),
    /** Email-permissioned hard-delete access (legacy: btnDelete2.Visible rule). */
    getLocalPCanHardDelete: b.query<DataResponse<{ can_delete: boolean }>, void>({
      query: () => '/purchase/local-p/access/can-delete',
    }),
  }),
})

export const {
  useListLocalPQuery,
  useSuggestLocalPContractNoQuery,
  useLazyCheckLocalPContractNoExistsQuery,
  useLazySuggestLocalPContractNoQuery,
  useGetLocalPQuery,
  useLazyGetLocalPQuery,
  useCreateLocalPMutation,
  useUpdateLocalPMutation,
  useDeleteLocalPMutation,
  useCancelLocalPMutation,
  useCopyLocalPMutation,
  useForceCloseLocalPMutation,
  useLazyGetLocalPPrintChooseQuery,
  useLazyPrintLocalPQuery,
  useGetLocalPCanHardDeleteQuery,
} = localPApi
